import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { ReactComponent as Zoom } from 'icons/zoom.svg';
import styles from './Slider.module.css';
import Lightbox from './Lightbox';

/**
 * Composant Slider pour afficher des galeries d'images
 * @param {Object} props
 * @param {Array} props.images - Tableau d'objets d'images avec { id, name, path, description }
 * @param {string} props.title - Titre optionnel de la galerie
 * @param {string} props.className - Classes CSS supplémentaires
 * @param {number} props.autoplaySpeed - Vitesse de défilement automatique en ms (0 = désactivé)
 */
const Slider = ({ 
  images = [], 
  title, 
  className,
  autoplaySpeed = 0 
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const autoplayTimerRef = useRef(null);
  
  const sliderRef = useRef(null);

  // Vérifie si des images sont disponibles
  const hasImages = images && images.length > 0;
  
  // Gestion swipe tactile - distance minimale pour considérer un swipe
  const minSwipeDistance = 50;

  // Gestion de la navigation
  const goToPrevious = useCallback(() => {
    if (!hasImages) return;
    
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
    
    // Réinitialiser le timer d'autoplay si activé
    resetAutoplayTimer();
  }, [hasImages, images.length]);

  const goToNext = useCallback(() => {
    if (!hasImages) return;
    
    setCurrentIndex((prevIndex) => 
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
    
    // Réinitialiser le timer d'autoplay si activé
    resetAutoplayTimer();
  }, [hasImages, images.length]);

  const goToSlide = (index) => {
    if (index === currentIndex) return;
    
    setCurrentIndex(index);
    
    // Réinitialiser le timer d'autoplay si activé
    resetAutoplayTimer();
  };

  // Ouvrir la lightbox pour afficher l'image en plein écran
  const openLightbox = () => {
    setIsLightboxOpen(true);
  };

  // Réinitialiser le timer d'autoplay
  const resetAutoplayTimer = () => {
    if (autoplayTimerRef.current) {
      clearInterval(autoplayTimerRef.current);
    }
    
    if (autoplaySpeed > 0) {
      autoplayTimerRef.current = setInterval(() => {
        goToNext();
      }, autoplaySpeed);
    }
  };

  // Gestion des événements tactiles
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrevious();
    }
  };

  // Défilement automatique
  useEffect(() => {
    if (autoplaySpeed > 0) {
      resetAutoplayTimer();
    }
    
    return () => {
      if (autoplayTimerRef.current) {
        clearInterval(autoplayTimerRef.current);
      }
    };
  }, [autoplaySpeed]);

  // Navigation par clavier
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [goToPrevious, goToNext]);

  // Si pas d'images, afficher un message
  if (!hasImages) {
    return (
      <div className={`${styles.sliderContainer} ${className || ''}`}>
        <div className={styles.emptyGallery}>
          <p>Aucune image disponible dans cette galerie</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.sliderContainer} ${className || ''}`}>
      {title && <h3 className={styles.galleryTitle}>{title}</h3>}
      
      <div 
        className={styles.sliderWrapper} 
        ref={sliderRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {images.map((image, index) => (
          <div 
            key={image.id || index}
            className={`${styles.slide} ${index === currentIndex ? styles.active : ''}`}
            role="group"
            aria-roledescription="slide"
            aria-label={`Diapositive ${index + 1} sur ${images.length}`}
            aria-hidden={index !== currentIndex}
          >
            <div className={styles.imageContainer}>
              <img 
                src={image.path} 
                alt={image.description || `Image ${index + 1}`} 
                className={styles.slideImage}
                loading={index === 0 ? "eager" : "lazy"}
              />
              {image.description && (
                <div className={styles.imageCaption}>
                  <p>{image.description}</p>
                </div>
              )}
            </div>
          </div>
        ))}
        
        {images.length > 1 && (
          <>
            <button 
              type="button" 
              className={`${styles.navButton} ${styles.prevButton}`}
              onClick={goToPrevious}
              aria-label="Image précédente"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            
            <button 
              type="button" 
              className={`${styles.navButton} ${styles.nextButton}`}
              onClick={goToNext}
              aria-label="Image suivante"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </>
        )}
        
        {/* Bouton Zoom */}
        <button
          type="button"
          className={styles.expandButton}
          onClick={openLightbox}
          aria-label="Agrandir l'image"
          title="Zoomer l'image"
        >
          <Zoom className={styles.zoomIcon} />
        </button>
      </div>
      
      {/* Pagination */}
      {images.length > 1 && (
        <div className={styles.pagination}>
          {images.map((_, index) => (
            <button
              key={index}
              type="button"
              className={`${styles.paginationDot} ${index === currentIndex ? styles.activeDot : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Aller à l'image ${index + 1}`}
            />
          ))}
        </div>
      )}
      
      {/* Lightbox */}
      {isLightboxOpen && (
        <Lightbox
          images={images}
          currentIndex={currentIndex}
          onClose={() => setIsLightboxOpen(false)}
          onNavigate={setCurrentIndex}
        />
      )}
    </div>
  );
};

export default Slider; 